import React from "react";
import SubHeading from "../common/SubHeading";
import { useFormSection, FormSectionProps } from "../../hooks/useFormSection";
import type { ActivityEntry } from "../../lib/types/IntakeFormTypes";

const activityLevelOptions = [
  { label: "Sedentary", value: "sedentary" },
  { label: "Moderately Active", value: "moderately" },
  { label: "Active", value: "active" },
  { label: "Very Active", value: "very" },
];

const PhysicalActivitySectionV2: React.FC<FormSectionProps> = (props) => {
  const { Field, formData, isEditMode = true } = useFormSection(props);

  const handleActivityChange = (
    activities: ActivityEntry[],
    index: number,
    field: keyof ActivityEntry,
    value: string
  ) => {
    const newActivity = [...activities];
    newActivity[index] = { ...newActivity[index], [field]: value };
    return newActivity;
  };

  const addActivity = (activities: ActivityEntry[]) => {
    return [
      ...activities,
      { activity: "", daysPerWeek: "", timeOfDay: "", duration: "" },
    ];
  };

  const removeActivity = (activities: ActivityEntry[], index: number) => {
    return activities.filter((_, i) => i !== index);
  };

  return (
    <section>
      <SubHeading>Physical Activity</SubHeading>
      <p className="mb-2 text-brand-stone-600">
        Explain in detail what type of resistance exercises, cardiovascular or
        sports activities you preform on average during a 7-day period:
      </p>
      
      <Field
        path="physicalActivity.activityTable"
        render={({ value = [], onChange }) => (
          <>
            <div className="space-y-2">
              {value.map((entry: ActivityEntry, index: number) => (
                <div
                  key={index}
                  className="flex flex-wrap items-end gap-2 p-3 mb-2 border rounded-lg bg-brand-stone-50"
                >
                  <div className="flex-1 min-w-[150px]">
                    <label className="text-sm font-medium">
                      Exercise/Activity
                    </label>
                    <input
                      type="text"
                      value={entry.activity}
                      onChange={(e) =>
                        onChange(handleActivityChange(value, index, "activity", e.target.value))
                      }
                      className={`w-full mt-1 p-2 border rounded ${isEditMode ? "bg-white" : "bg-gray-100"}`}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <label className="text-sm font-medium">Days/week</label>
                    <input
                      type="text"
                      value={entry.daysPerWeek}
                      onChange={(e) =>
                        onChange(handleActivityChange(value, index, "daysPerWeek", e.target.value))
                      }
                      className={`w-full mt-1 p-2 border rounded ${isEditMode ? "bg-white" : "bg-gray-100"}`}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <label className="text-sm font-medium">Time of day</label>
                    <input
                      type="text"
                      value={entry.timeOfDay}
                      onChange={(e) =>
                        onChange(handleActivityChange(value, index, "timeOfDay", e.target.value))
                      }
                      className={`w-full mt-1 p-2 border rounded ${isEditMode ? "bg-white" : "bg-gray-100"}`}
                      disabled={!isEditMode}
                    />
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <label className="text-sm font-medium">Duration</label>
                    <input
                      type="text"
                      value={entry.duration}
                      onChange={(e) =>
                        onChange(handleActivityChange(value, index, "duration", e.target.value))
                      }
                      className={`w-full mt-1 p-2 border rounded ${isEditMode ? "bg-white" : "bg-gray-100"}`}
                      disabled={!isEditMode}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onChange(removeActivity(value, index))}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 h-10"
                    aria-label={`Remove activity ${index + 1}`}
                    disabled={!isEditMode}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => onChange(addActivity(value))}
              className="bg-brand-green-600 text-white px-4 py-2 rounded hover:bg-brand-green-700 mt-2"
              disabled={!isEditMode}
            >
              + Add Activity
            </button>
          </>
        )}
      />
      
      <Field
        path="physicalActivity.furtherNotes"
        label="Add any further notes here regarding your level of exercise and training:"
        type="textarea"
        rows={3}
      />
      
      <Field
        path="physicalActivity.activityLevel"
        label="How would you rate your activity level, including what you do during the day (take into account your job if it is physical in nature) Select one"
        type="radio"
        options={activityLevelOptions}
      />
      
      <Field
        path="physicalActivity.fatigueAfterExercise"
        label="Do you feel fatigued after exercise? If so, describe"
        type="textarea"
        rows={3}
      />
    </section>
  );
};

export default PhysicalActivitySectionV2;